import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Box,
  Button,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import unifiedApi from '../../utils/unifiedApi';

const SLOTS_PER_PAGE = 6;
const SPARE_NAME_COL_WIDTH = 180;
const GAMES_PLAYED_COL_WIDTH = 110;

/**
 * SpareAvailabilityTable - Displays a matrix of spare pool members and their availability for time slots
 * 
 * @param {number} leagueId - The league whose spare pool is shown
 * @param {boolean} canManage - Whether the current user can manage availability
 */
const SpareAvailabilityTable = ({ leagueId, canManage = false, canViewAll = false, startDate = null, endDate = null, showAvailable = true, showUnavailable = true, showNoResponse = true }) => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination state
  const [slotPage, setSlotPage] = useState(0);

  // Games Played column sort: null (default order), 'asc', or 'desc'
  const [gamesPlayedSortDir, setGamesPlayedSortDir] = useState(null);
  const handleToggleGamesPlayedSort = () => {
    setGamesPlayedSortDir(prev => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  };



  // Load availability matrix
  const loadMatrix = useCallback(async () => {
    if (!leagueId) return;
    
    try {
      setLoading(true);
      setError(null);
      const options = {};
      if (startDate) options.startDate = startDate;
      if (endDate) options.endDate = endDate;
      const data = await unifiedApi.getSpareAvailabilityMatrix(leagueId, options);
      setMatrix(data);
    } catch (err) {
      console.error('Error loading availability matrix:', err);
      setError('Failed to load availability data');
    } finally {
      setLoading(false);
    }
  }, [leagueId, startDate, endDate]);

  // Initial load
  React.useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  // Toggle availability for a member at a time slot
  const handleToggleAvailability = async (memberId, timeSlotId, currentValue) => {
    // Toggle: null -> true -> false -> null
    let newValue;
    if (currentValue === null || currentValue === undefined) {
      newValue = true;
    } else if (currentValue === true) {
      newValue = false;
    } else {
      newValue = null;
    }

    try {
      await unifiedApi.setSparePoolMemberAvailability(memberId, [
        { spareTimeSlotId: timeSlotId, isAvailable: newValue }
      ]);
      
      // Update local state
      setMatrix(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          members: prev.members.map(m => {
            if (m.memberId === memberId) {
              return {
                ...m,
                availability: {
                  ...m.availability,
                  [timeSlotId]: newValue
                }
              };
            }
            return m;
          })
        };
      });
    } catch (err) {
      console.error('Error updating availability:', err);
      setError('Failed to update availability');
    }
  };



  // Absolute slot start; the API's startTime is tenant-local without an offset
  const getSlotStart = (slot) => new Date(slot.startTimeUtc || slot.startTime);

  // Render availability status icon; editable statuses are keyboard-accessible buttons
  const renderAvailabilityIcon = (isAvailable, canEdit, onClick, label = '') => {
    const iconSx = { fontSize: 24 };
    const statusText = isAvailable === true ? 'Available' : isAvailable === false ? 'Not available' : 'No response';

    let icon;
    if (isAvailable === true) {
      icon = <CheckCircleIcon sx={{ ...iconSx, color: 'success.main' }} />;
    } else if (isAvailable === false) {
      icon = <CancelIcon sx={{ ...iconSx, color: 'error.main' }} />;
    } else {
      icon = <HelpOutlineIcon sx={{ ...iconSx, color: 'warning.main' }} />;
    }

    if (!canEdit) {
      return (
        <Tooltip title={statusText}>
          {icon}
        </Tooltip>
      );
    }

    return (
      <Tooltip title={isAvailable === null || isAvailable === undefined ? 'Click to set availability' : 'Click to change'}>
        <IconButton
          size="small"
          onClick={onClick}
          aria-label={`Change availability for ${label}, currently ${statusText.toLowerCase()}`}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  };

  // Format date/time for display
  const formatTimeSlot = (startTime) => {
    const date = new Date(startTime);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    });
    return { dateStr, timeStr };
  };

  // Format an appearance date for the Games Played tooltip
  const formatAppearanceDate = (appearanceDate) => {
    return new Date(appearanceDate).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Render the Games Played count with a hover tooltip listing each game
  const renderGamesPlayedCell = (member) => {
    const appearances = member.appearances || [];
    if (appearances.length === 0) {
      return <Typography variant="body2">{member.gamesPlayed}</Typography>;
    }
    return (
      <Tooltip
        title={
          <Box>
            {appearances.map((a, idx) => (
              <Typography key={idx} variant="caption" display="block">
                {formatAppearanceDate(a.appearanceDate)} - {a.teamName}{a.positionPlayed ? ` - ${a.positionPlayed}` : ''}
              </Typography>
            ))}
          </Box>
        }
      >
        <Typography variant="body2" sx={{ textDecoration: 'underline dotted', cursor: 'default' }}>
          {member.gamesPlayed}
        </Typography>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!matrix) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No availability data available</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Self-only visibility notice for non-coordinators */}
      {!canManage && !canViewAll && matrix.members.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can only see your own availability. Tap a date to mark yourself available, unavailable, or clear your response. Contact a Spares Coordinator to view or manage the full schedule.
        </Alert>
      )}

      {/* No time slots message */}
      {matrix.timeSlots.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {startDate
            ? 'No time slots found for this date.'
            : canManage
              ? 'No time slots found. Time slots sync automatically from the league schedule every 15 minutes.'
              : 'No spare dates yet. They will appear here once the league schedule is published.'}
        </Alert>
      ) : matrix.members.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No members in this spare pool. Add members to track their availability.
        </Alert>
      ) : (() => {
        const now = new Date();
        // When a specific date range is provided, show all returned slots (including past ones);
        // otherwise only show future slots to avoid clutter on the standalone view.
        const visibleSlots = startDate
          ? matrix.timeSlots
          : matrix.timeSlots.filter(slot => getSlotStart(slot) >= now);
        const totalSlots = visibleSlots.length;

        if (totalSlots === 0) {
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              {startDate
                ? 'No time slots found for this date.'
                : canManage
                  ? 'No upcoming time slots. Time slots sync automatically from the league schedule every 15 minutes.'
                  : 'No upcoming spare dates. They will appear here once the league schedule is published.'}
            </Alert>
          );
        }

        const totalPages = Math.ceil(totalSlots / SLOTS_PER_PAGE);
        const pagedSlots = visibleSlots.slice(slotPage * SLOTS_PER_PAGE, (slotPage + 1) * SLOTS_PER_PAGE);

        return (
          <>
            {/* Pagination controls */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Button
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  onClick={() => setSlotPage(p => Math.max(0, p - 1))}
                  disabled={slotPage === 0}
                >
                  Previous
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {slotPage * SLOTS_PER_PAGE + 1}&ndash;{Math.min((slotPage + 1) * SLOTS_PER_PAGE, totalSlots)} of {totalSlots} slots
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => setSlotPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={slotPage >= totalPages - 1}
                >
                  Next
                </Button>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Games Played reflects games where the team manager included the spare on their match report.
              Future games are not counted until reported.
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 3, 
                        backgroundColor: 'background.paper',
                        minWidth: SPARE_NAME_COL_WIDTH
                      }}
                    >
                      Spare
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        left: SPARE_NAME_COL_WIDTH,
                        zIndex: 3,
                        backgroundColor: 'background.paper',
                        minWidth: GAMES_PLAYED_COL_WIDTH
                      }}
                    >
                      <TableSortLabel
                        active={gamesPlayedSortDir !== null}
                        direction={gamesPlayedSortDir || 'asc'}
                        onClick={handleToggleGamesPlayedSort}
                      >
                        Games Played
                      </TableSortLabel>
                    </TableCell>

                    {pagedSlots.map((slot) => {
                      const { dateStr, timeStr } = formatTimeSlot(slot.startTime);
                      return (
                        <TableCell 
                          key={slot.id} 
                          align="center"
                          sx={{ minWidth: 100 }}
                        >
                          <Typography variant="caption" display="block">
                            {dateStr}
                          </Typography>
                          <Typography variant="caption" display="block" fontWeight="bold">
                            {timeStr}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matrix.members.filter((member) => {
                    // Get all availability values for displayed time slots
                    const statuses = pagedSlots.map(slot => member.availability[slot.id]);
                    // If member has no slots, treat as no response
                    if (statuses.length === 0) return showNoResponse;
                    // Show member if any of their slot statuses match an active filter
                    return statuses.some(val => {
                      if (val === true) return showAvailable;
                      if (val === false) return showUnavailable;
                      return showNoResponse; // null/undefined
                    });
                  }).sort((a, b) => {
                    if (gamesPlayedSortDir === null) return 0;
                    const diff = (a.gamesPlayed || 0) - (b.gamesPlayed || 0);
                    return gamesPlayedSortDir === 'asc' ? diff : -diff;
                  }).map((member) => (
                    <TableRow key={member.memberId} hover>
                      <TableCell 
                        sx={{ 
                          position: 'sticky', 
                          left: 0, 
                          zIndex: 1, 
                          backgroundColor: 'background.paper' 
                        }}
                      >
                        <Link 
                          to={`/player/${member.userTenantId}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <Typography 
                            variant="body2"
                            sx={{ 
                              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                              cursor: 'pointer'
                            }}
                          >
                            {member.firstName} {member.lastName}
                          </Typography>
                        </Link>
                        {member.assignedTeamName && (
                          <Chip
                            icon={<GroupsIcon />}
                            label={member.assignedTeamName}
                            size="small"
                            color="info"
                            variant="outlined"
                            clickable={false}
                            onClick={() => {}}
                            sx={{ mt: 0.5, maxWidth: '100%' }}
                          />
                        )}
                        {(member.preferredPosition || member.secondPreferredPosition) && (
                          <Typography variant="caption" color="text.secondary">
                            {[member.preferredPosition, member.secondPreferredPosition].filter(Boolean).join(', ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          position: 'sticky',
                          left: SPARE_NAME_COL_WIDTH,
                          zIndex: 1,
                          backgroundColor: 'background.paper'
                        }}
                      >
                        {renderGamesPlayedCell(member)}
                      </TableCell>

                      {pagedSlots.map((slot) => {
                        const { dateStr, timeStr } = formatTimeSlot(slot.startTime);
                        return (
                          <TableCell key={slot.id} align="center">
                            {renderAvailabilityIcon(
                              member.availability[slot.id],
                              canManage || (member.isCurrentUser && getSlotStart(slot) > now),
                              () => handleToggleAvailability(member.memberId, slot.id, member.availability[slot.id]),
                              `${member.firstName} ${member.lastName} on ${dateStr} at ${timeStr}`
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      })()}



      {/* Success/Error snackbars */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!success} 
        autoHideDuration={3000} 
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpareAvailabilityTable;
